/**
 * TutorialHighlight Component
 *
 * Creates a spotlight effect on tutorial target elements with:
 * - Dark semi-transparent overlay covering the screen
 * - Cutout/spotlight around the target element
 * - Pulsing animation on the target element
 * - Blocks interaction with non-target elements
 */

import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const TutorialHighlight = ({
  selector,           // CSS selector for the target element
  isActive = false,   // Whether the highlight is active
  onTargetClick,      // Optional callback when target is clicked
  onDismiss,          // Optional callback when clicking outside target (to dismiss)
  padding = 8,        // Padding around the highlighted element
  pulseColor = 'rgba(239, 68, 68, 0.6)' // Color for the pulse effect (red by default)
}) => {
  const [targetRect, setTargetRect] = useState(null);
  const [targetElement, setTargetElement] = useState(null);

  // Find and track the target element
  const updateTargetPosition = useCallback(() => {
    if (!selector || !isActive) {
      setTargetRect(null);
      setTargetElement(null);
      return;
    }

    const element = document.querySelector(selector);
    if (element) {
      const rect = element.getBoundingClientRect();
      setTargetRect({
        top: rect.top - padding,
        left: rect.left - padding,
        width: rect.width + padding * 2,
        height: rect.height + padding * 2,
        originalTop: rect.top,
        originalLeft: rect.left,
        originalWidth: rect.width,
        originalHeight: rect.height
      });
      setTargetElement(element);
    } else {
      console.warn('[TutorialHighlight] Element not found:', selector);
      setTargetRect(null);
      setTargetElement(null);
    }
  }, [selector, isActive, padding]);

  // Update position on mount and when selector changes
  useEffect(() => {
    updateTargetPosition();

    // Update on scroll and resize
    window.addEventListener('scroll', updateTargetPosition, true);
    window.addEventListener('resize', updateTargetPosition);

    // Also update periodically in case of dynamic content
    const interval = setInterval(updateTargetPosition, 500);

    return () => {
      window.removeEventListener('scroll', updateTargetPosition, true);
      window.removeEventListener('resize', updateTargetPosition);
      clearInterval(interval);
    };
  }, [updateTargetPosition]);

  // Add pulse class to target element
  useEffect(() => {
    if (targetElement && isActive) {
      targetElement.classList.add('tutorial-highlight-target');
      targetElement.style.position = 'relative';
      targetElement.style.zIndex = '60';

      return () => {
        targetElement.classList.remove('tutorial-highlight-target');
        targetElement.style.position = '';
        targetElement.style.zIndex = '';
      };
    }
  }, [targetElement, isActive]);

  // Handle click on target element
  const handleOverlayClick = useCallback((e) => {
    // Check if click is within target bounds
    if (targetRect) {
      const clickX = e.clientX;
      const clickY = e.clientY;

      if (
        clickX >= targetRect.originalLeft &&
        clickX <= targetRect.originalLeft + targetRect.originalWidth &&
        clickY >= targetRect.originalTop &&
        clickY <= targetRect.originalTop + targetRect.originalHeight
      ) {
        // Click is on target - allow it through
        if (onTargetClick) {
          onTargetClick();
        }
        // Let the click pass through to the actual element
        return;
      }
    }
    // Click is outside target - call onDismiss if provided
    if (onDismiss) {
      onDismiss();
    }
    e.preventDefault();
    e.stopPropagation();
  }, [targetRect, onTargetClick, onDismiss]);

  if (!isActive || !targetRect) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="tutorial-highlight-overlay"
        onClick={handleOverlayClick}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 55,
          pointerEvents: 'auto'
        }}
      >
        {/* Dark overlay with cutout using CSS clip-path */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            // Create cutout using clip-path with polygon
            clipPath: `polygon(
              0% 0%,
              0% 100%,
              ${targetRect.left}px 100%,
              ${targetRect.left}px ${targetRect.top}px,
              ${targetRect.left + targetRect.width}px ${targetRect.top}px,
              ${targetRect.left + targetRect.width}px ${targetRect.top + targetRect.height}px,
              ${targetRect.left}px ${targetRect.top + targetRect.height}px,
              ${targetRect.left}px 100%,
              100% 100%,
              100% 0%
            )`,
            pointerEvents: 'auto'
          }}
        />

        {/* Pulse ring around target */}
        <motion.div
          initial={{ scale: 1, opacity: 0.8 }}
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.8, 0.4, 0.8]
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: 'easeInOut'
          }}
          style={{
            position: 'absolute',
            top: targetRect.top - 4,
            left: targetRect.left - 4,
            width: targetRect.width + 8,
            height: targetRect.height + 8,
            borderRadius: '12px',
            border: `3px solid ${pulseColor}`,
            boxShadow: `0 0 20px ${pulseColor}, 0 0 40px ${pulseColor}`,
            pointerEvents: 'none'
          }}
        />

        {/* Inner glow */}
        <motion.div
          animate={{
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: 'easeInOut'
          }}
          style={{
            position: 'absolute',
            top: targetRect.top,
            left: targetRect.left,
            width: targetRect.width,
            height: targetRect.height,
            borderRadius: '8px',
            boxShadow: `inset 0 0 20px ${pulseColor}`,
            pointerEvents: 'none'
          }}
        />

        {/* Clickable area over target - captures click, clicks actual element, then triggers callback */}
        <div
          onClick={(e) => {
            e.stopPropagation();
            console.log('[TutorialHighlight] Clickable overlay clicked');
            // Click the actual target element FIRST (before any state changes)
            if (targetElement) {
              console.log('[TutorialHighlight] Clicking target element:', targetElement);
              targetElement.click();
            }
            // Then call the onTargetClick callback (which may clear highlight/change state)
            if (onTargetClick) {
              console.log('[TutorialHighlight] Calling onTargetClick');
              onTargetClick();
            }
          }}
          style={{
            position: 'fixed',
            top: targetRect.originalTop,
            left: targetRect.originalLeft,
            width: targetRect.originalWidth,
            height: targetRect.originalHeight,
            pointerEvents: 'auto', // Capture clicks on target
            zIndex: 9999, // Very high to ensure it's above everything
            cursor: 'pointer'
          }}
        />
      </motion.div>
    </AnimatePresence>
  );
};

export default TutorialHighlight;
